import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './hooks/useAuth';
import supabase from './integrations/supabase';
import apiService from './services/api';
import { LoginSkeleton } from './components/SkeletonLoader';
import BackendErrorHandler from './components/BackendErrorHandler';

import Login from './pages/login.js';
import Dashboard from './pages/Dashboard';
import Quiz from './pages/Quiz';
import Notes from './pages/Notes';
import Leaderboard from './pages/Leaderboard';
import Planner from './pages/Planner';
import Calendar from './pages/Calendar';
import GroupStudy from './pages/GroupStudy';
import Reading from './pages/Reading';

import Multiplayer from './pages/Multiplayer';
import GameRoom from './pages/GameRoom';
import ProfileSettings from './pages/ProfileSettings';
import MyProgress from './pages/MyProgress';
import Settings from './pages/Settings';
import HabitHub from './pages/HabitHub';
import AdvancedFeatures from './pages/AdvancedFeatures';
import RAGDashboard from './pages/RAGDashboard';
import TestingDashboard from './pages/TestingDashboard';
import AdvancedRAGDashboard from './pages/AdvancedRAGDashboard';

import UserStories from './pages/UserStories';
import Header from './components/Header';
import Footer from './components/Footer';
import { useTheme, ThemeProvider } from './utils/theme-context';
import UploadFiles from './pages/UploadFiles';
import ClassSelection from './pages/courseSelection';
import TeacherDashboard from './pages/TeacherDashboard';
import Study from './pages/Study';
import StudyContent from './pages/StudyContent';
import Flashcards from './pages/Flashcards';
import Upload from './pages/Upload';
import IntegrationTest from './pages/IntegrationTest';
import SupabaseStorageTest from './components/SupabaseStorageTest';
import SupabaseAuthTest from './components/SupabaseAuthTest';
import EnvVarTest from './components/EnvVarTest';
import TTSTest from './pages/TTSTest';


// Theme initialization component
const ThemeInitializer = ({ children }) => {
  const [themeReady, setThemeReady] = useState(false);

  useEffect(() => {
    // Initialize theme synchronously
    const root = document.documentElement;
    const stored = localStorage.getItem('darkMode');
    
    if (stored !== null) {
      const shouldBeDark = stored === 'true';
      if (shouldBeDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    } else {
      // Use system preference as default
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (systemPrefersDark) {
        root.classList.add('dark');
        localStorage.setItem('darkMode', 'true');
      } else {
        root.classList.remove('dark');
        localStorage.setItem('darkMode', 'false');
      }
    }
    
    setThemeReady(true);
  }, []);

  if (!themeReady) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return children;
};

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 },
};

function PageWithHeader({ children }) {
  const { theme } = useTheme();
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: theme.background,
        backgroundSize: theme.backgroundSize,
        color: theme.textColor,
        fontFamily: theme.fontFamily,
      }}
    >
      <Header />
      <main
        className="flex-1 flex flex-col items-center justify-start pt-20 pb-8 px-2 w-full"
        style={{ color: theme.textColor, fontFamily: theme.fontFamily }}
      >
        {children}
      </main>
      <Footer />
    </div>
  );
}

// Layout for public pages (login, course selection) with only Footer
function PageWithFooter({ children }) {
  const { theme } = useTheme();
  
  // Force light mode for onboarding pages
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    localStorage.setItem('darkMode', 'false');
    
    return () => {
      // Restore theme when component unmounts
      const stored = localStorage.getItem('darkMode');
      if (stored === 'true') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };
  }, []);
  
  return (
    <div
      className="min-h-screen flex flex-col bg-white"
      style={{
        color: theme.textColor,
        fontFamily: theme.fontFamily,
      }}
    >
      <main className="flex-1 flex flex-col items-center justify-center w-full">
        {children}
      </main>
      <Footer />
    </div>
  );
}

// Layout for course selection - no header, no footer, clean interface
function PageWithNoHeaderFooter({ children }) {
  const { theme } = useTheme();
  
  // Force light mode for onboarding pages
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    localStorage.setItem('darkMode', 'false');
    
    return () => {
      // Restore theme when component unmounts
      const stored = localStorage.getItem('darkMode');
      if (stored === 'true') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };
  }, []);
  
  return (
    <div
      className="min-h-screen flex flex-col bg-white"
      style={{
        color: theme.textColor,
        fontFamily: theme.fontFamily,
      }}
    >
      <main className="flex-1 flex flex-col items-center justify-center w-full">
        {children}
      </main>
    </div>
  );
}

// Cache for course selection to avoid repeated database calls
const courseSelectionCache = new Map();

// OAuth Callback Component
function AuthCallback() {
  const navigate = useNavigate();
  const { handleOAuthLogin } = useAuth();
  const [status, setStatus] = useState('Processing authentication...');
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let hasProcessed = false;

    const handleAuthCallback = async () => {
      // Prevent multiple executions
      if (hasProcessed) return;
      hasProcessed = true;

      try {
        

        // Check if we have auth tokens in URL hash (OAuth callback)
        if (window.location.hash.includes('access_token')) {

          try {
            // Parse the hash parameters
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const refreshToken = hashParams.get('refresh_token');



            if (accessToken) {

              // Set the session manually
              const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
              });

              if (sessionError) {
                console.error('❌ AuthCallback: Error setting session:', sessionError);
              } else {
  
              }
            }
          } catch (parseError) {
            console.error('❌ AuthCallback: Error parsing URL hash:', parseError);
          }
        } else {

        }
        
        // Get the current session
        
        const { data, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        
        
        if (error) {
          console.error('❌ AuthCallback error:', error);
          if (isMounted) {
            setError('Authentication failed. Please try again.');
          }
          return;
        }
        
        if (data.session?.user) {
          
          if (isMounted) {
            setStatus('Authentication successful! Redirecting...');
          }
          
          // Store the session token for backend API
          localStorage.setItem('user_token', data.session.access_token);
          localStorage.setItem('refresh_token', data.session.refresh_token);
          
          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname);
          
          // Check if user has course selection using the new API
          try {
  
            
            // Use the API to check course selection
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/v1/course-selections/check-user-selection`, {
              headers: {
                'Authorization': `Bearer ${data.session.access_token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (!isMounted) return;
            
            if (response.ok) {
              const courseData = await response.json();
              
              if (courseData.has_selection) {
  
                if (isMounted) {
                  setTimeout(() => navigate('/dashboard'), 1000);
                }
                return;
              } else {

                if (isMounted) {
                  setTimeout(() => navigate('/course-selection'), 1000);
                }
                return;
              }
            } else {

              if (isMounted) {
                setTimeout(() => navigate('/course-selection'), 1000);
              }
            }
          } catch (error) {
            
            if (isMounted) {
              setTimeout(() => navigate('/course-selection'), 1000);
            }
          }
        } else {
          
          if (isMounted) {
            setError('Authentication failed. No user data received.');
          }
        }
        
      } catch (error) {
        console.error('❌ AuthCallback error:', error);
        if (isMounted) {
          setError('Authentication failed. Please try again.');
        }
      }
    };
    
    handleAuthCallback();

    return () => {
      isMounted = false;
    };
  }, [navigate]); // Remove handleOAuthLogin from dependencies to prevent infinite loop

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Completing Login</h2>
          <p className="text-gray-600">{status}</p>
        </div>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (!hasChecked && !loading) {
      setHasChecked(true);
    }
  }, [loading, hasChecked]);

  

  if (loading || !hasChecked) {
    
    return <LoginSkeleton />;
  }

  if (!user) {
    
    // Always redirect to login if no user, regardless of current path
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  
  return children;
}

// Component to check if user needs course selection
function RequireCourseSelection({ children }) {
  const { user, loading, hasCourseSelection, forceRefreshCourseSelection } = useAuth();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    

    // Don't check if on login or course selection pages
    if (location.pathname === '/login' || location.pathname === '/course-selection') {
      
      return;
    }

    if (!user || loading || redirecting || hasChecked) return;

    // If user has no course selection, redirect to course selection page
    if (hasCourseSelection === false) {
      
      setRedirecting(true);
      setHasChecked(true);
      navigate('/course-selection', { replace: true });
      return;
    }

    // If course selection status is still loading, try to force refresh
    if (hasCourseSelection === null) {
      
      forceRefreshCourseSelection();
      return;
    }

    
    setHasChecked(true);
  }, [user, loading, hasCourseSelection, navigate, redirecting, hasChecked, location.pathname]);

  // Reset redirecting flag when location changes
  useEffect(() => {
    if (location.pathname === '/course-selection') {
      setRedirecting(false);
      setHasChecked(false);
    }
  }, [location.pathname]);

  // Don't show loading skeleton on course selection page
  if (location.pathname === '/course-selection') {
    return children;
  }

  if (loading || hasCourseSelection === null || redirecting || !hasChecked) {
    return <LoginSkeleton />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user has no course selection, show loading while redirecting
  if (hasCourseSelection === false) {
    return <LoginSkeleton />;
  }

  return children;
}

function AnimatedRoutes() {
  const location = useLocation();
  

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/login" element={<PageWithFooter><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Login /></motion.div></PageWithFooter>} />
        <Route path="/course-selection" element={<PageWithNoHeaderFooter><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><ClassSelection /></motion.div></PageWithNoHeaderFooter>} />
        <Route path="/teacher-dashboard" element={<RequireCourseSelection><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><TeacherDashboard /></motion.div></PageWithHeader></RequireCourseSelection>} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/dashboard" element={<RequireCourseSelection><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Dashboard /></motion.div></PageWithHeader></RequireCourseSelection>} />
          <Route path="/quiz/:subject" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Quiz /></motion.div></PageWithHeader></RequireAuth>} />

          <Route path="/notes" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Notes /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/leaderboard" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Leaderboard /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/planner" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Planner /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/calendar" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Calendar /></motion.div></PageWithHeader></RequireAuth>} />
                    <Route path="/multiplayer" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Multiplayer /></motion.div></PageWithHeader></RequireAuth>} />
            <Route path="/game-room" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><GameRoom /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/group-study" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><GroupStudy /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/reading" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Reading /></motion.div></PageWithHeader></RequireAuth>} />


        <Route path="/study/:subject/:book" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Study /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/study/:subject" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Study /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/study-content/:subject/:chapter" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><StudyContent /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/flashcards/:subject" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Flashcards /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/upload/:subject" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Upload /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/my-progress" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><MyProgress /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><Settings /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/habit-hub" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><HabitHub /></motion.div></PageWithHeader></RequireAuth>} />

        <Route path="/test" element={<PageWithFooter><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><IntegrationTest /></motion.div></PageWithFooter>} />
        <Route path="/storage-test" element={<RequireAuth><PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><SupabaseStorageTest /></motion.div></PageWithHeader></RequireAuth>} />
        <Route path="/auth-test" element={<PageWithFooter><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><SupabaseAuthTest /></motion.div></PageWithFooter>} />
        <Route path="/env-var-test" element={<PageWithFooter><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><EnvVarTest /></motion.div></PageWithFooter>} />
<Route path="/tts-test" element={<PageWithHeader><motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={{ duration: 0.4 }}><TTSTest /></motion.div></PageWithHeader>} />
<Route path="/" element={<RequireAuth><Navigate to="/dashboard" replace /></RequireAuth>} />
<Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </AnimatePresence>
  );
}

function AppContainer() {
  const { theme } = useTheme();
  

  return (
    <div
      style={{
        background: theme.background,
        backgroundSize: theme.backgroundSize,
        minHeight: '100vh',
        color: theme.textColor,
        fontFamily: theme.fontFamily,
      }}
    >
      <AnimatedRoutes />
    </div>
  );
}

function App() {

  
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ThemeInitializer>
            <BackendErrorHandler>
              <AppContainer />
            </BackendErrorHandler>
          </ThemeInitializer>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
